import * as stylex from "@stylexjs/stylex";
import { motion } from "framer-motion";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useRTL } from "../../hooks";
import { breakpoints, colors } from "../../materials/consts.stylex";
import { typography } from "../../materials/typography.stylex";
import { rowStyles, sectionStyles, stylexStyles } from "./shared/table";
const linkArrow = new URL("../../icons/svg/ic_link_24dp.svg", import.meta.url).href;
// -----------------------------------------------------------------------------
// IndicatorSection
export const IndicatorSection = ({ children })=>{
    const isRTL = useRTL();
    return <div {...stylex.props(sectionStyles.root)}>
      <div {...stylex.props(stylexStyles.table)}>
        <div className={`indicator-section-content ${stylex.props(sectionStyles.content, isRTL ? sectionStyles.contentRtl : sectionStyles.contentLtr).className}`}>
          {children}
        </div>
      </div>
    </div>;
};
// -----------------------------------------------------------------------------
// IndicatorTable
const tableStyles = {
    small: {
        kMwMTN: "x9o7od0",
        k1xSpc: "x1s85apg xv1d9aq",
        $$css: true
    },
    smallRtl: {
        koQZXg: "x1gh5o4n",
        km5ZXQ: "xy7owz0",
        $$css: true
    },
    smallLtr: {
        km5ZXQ: "x1rmmofs",
        koQZXg: "xt0o90j",
        $$css: true
    },
    smallLastRtl: {
        kFhvOy: "x15bdqvn",
        km5ZXQ: "xack27t",
        koQZXg: "xj3b58b",
        $$css: true
    },
    smallLastLtr: {
        kFhvOy: "x15bdqvn",
        koQZXg: "x8x9d4c",
        $$css: true
    }
};
export const IndicatorTable = ({ children })=>{
    const isRTL = useRTL();
    const smallProps = stylex.props(typography.meta, tableStyles.small, isRTL ? tableStyles.smallRtl : tableStyles.smallLtr);
    const lastSmallProps = stylex.props(typography.meta, tableStyles.small, isRTL ? tableStyles.smallRtl : tableStyles.smallLtr, isRTL ? tableStyles.smallLastRtl : tableStyles.smallLastLtr);
    return <div className="xrvj5dj xqui205">
      <div className="x78zum5 x1nhvcw1 xdpxx8g xdj266r x1yf7rl7 xd6udtm xat24cr x61cp00 xet2fuk x7bz8ia">
        <small {...smallProps}>
          <FormattedMessage id="Indicator" defaultMessage="Indicator"/>
        </small>
        <small {...smallProps}>
          <FormattedMessage id="Description" defaultMessage="Description"/>
        </small>
        <small {...lastSmallProps}>
          <FormattedMessage id="Link to UIS data" defaultMessage="Link to UIS data"/>
        </small>
      </div>
      {children}
    </div>;
};
// -----------------------------------------------------------------------------
// IndicatorRow
const rowLinkStyles = {
    row: {
        kVAEAm: "x1n2onr6",
        $$css: true
    }
};
export const IndicatorRow = ({ children, id, global, link })=>{
    const [isTarget, setTarget] = React.useState(false);
    const isRTL = useRTL();
    const intl = useIntl();
    React.useEffect(()=>{
        const locationHash = window.location.hash;
        if (locationHash.length > 1) {
            setTarget(locationHash.slice(1) === id);
        }
    }, [
        id
    ]);
    const parentVariants = {
        hover: {
            x: 0
        },
        default: {
            x: 0
        }
    };
    const childVariants = {
        hover: {
            x: isRTL ? -5 : 5,
            rotate: isRTL ? 180 : 0
        },
        default: {
            x: 0,
            rotate: isRTL ? 180 : 0
        }
    };
    const content = <>
      { /* IndicatorTag is rendered directly; `children` is MDX — its <p>'s type styles
       * live in style.global.css, scoped by this marker class. */ }
      <div className={`indicator-row-content ${stylex.props(rowStyles.mainContent, isRTL ? rowStyles.mainContentPaddingRtl : rowStyles.mainContentPaddingLtr).className}`}>
        <IndicatorTag id={id} global={global}/>
        {children}
      </div>
      <div {...stylex.props(rowStyles.linkArrow)}>
        {link && <a href={link} aria-label={`${intl.formatMessage({
        id: "Link to UIS data",
        defaultMessage: "Link to UIS data"
    })} ${id}`} className="x78zum5 x6s0dn4 x1heor9g x1hl2dhg x1s928wv x1j6awrg xarstr8 x2jd71m x1jdy9wv">
            <span {...stylex.props(rowStyles.link)}>
              <FormattedMessage id="Link to UIS data" defaultMessage="Link to UIS data"/>
            </span>
            <motion.img {...stylex.props(rowStyles.linkImg, isRTL ? rowStyles.linkImgRtl : rowStyles.linkImgLtr)} initial="default" variants={childVariants} src={linkArrow} alt=""/>
          </a>}
      </div>
    </>;
    return <div id={id} {...stylex.props(isTarget && rowStyles.rootHighlighted)}>
      {link ? <motion.div {...stylex.props(rowStyles.row, rowStyles.rowLink, rowLinkStyles.row)} variants={parentVariants} whileHover="hover">
          {content}
        </motion.div> : <div {...stylex.props(rowStyles.row)}>{content}</div>}
    </div>;
};
// -----------------------------------------------------------------------------
// IndicatorTag
const tagStyles = {
    root: {
        k1xSpc: "x78zum5",
        k63SB2: "x117nqv4",
        kMwMTN: "x1vc0dw",
        kmVPX3: "x17qkr04",
        kZKoxP: "xxk0z11 xw2u99y",
        k7Eaqz: "x1jn76jz",
        kjj79g: "xl56j7k",
        kGNEyG: "x6s0dn4",
        k1K539: "x1e56ztr x16ko1ya",
        $$css: true
    },
    global: {
        kWkggS: "x1ln1k1l",
        kaIpWk: "x12oqio5",
        $$css: true
    },
    subtheme: {
        kWkggS: "x1vrwm76",
        kaIpWk: "xgqmno8",
        $$css: true
    },
    marginRtl: {
        koQZXg: "xq1n1xh",
        km5ZXQ: "x1yf7rl7",
        $$css: true
    },
    marginLtr: {
        km5ZXQ: "x1o6q14k",
        koQZXg: "xj3b58b",
        $$css: true
    }
};
export const IndicatorTag = (props)=>{
    const isRTL = useRTL();
    return <span {...stylex.props(typography.body2, tagStyles.root, props.global ? tagStyles.global : tagStyles.subtheme, isRTL ? tagStyles.marginRtl : tagStyles.marginLtr)}>
      {props.id}
    </span>;
};
